/**
 * MergeRenderer — read-only render for MERGE statements (MSSQL, PG 15+,
 * Oracle). Layout:
 *   - Target card (`MERGE INTO target [alias]`)
 *   - Source card (`USING source [alias]`)
 *   - ON predicate pill row (non-editable)
 *   - One `WhenBranchCard` per WHEN branch (in source order)
 *   - `[+ Add WHEN]` footer — a real dialect-aware template menu
 *     (`AddWhenBranchMenu`) once `editReady`, else the § 1.4 disabled
 *     scaffold with a reason-specific tooltip (§2.5 S7)
 *
 * If parsing fails (no target / no source / no ON), renders a small fallback
 * surface — the Visual view still offers Edit as SQL.
 */

import type { ParseOptions, StatementInfo } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { applyMergeConvert } from '../../edit/edits/applyMergeConvert';
import { convertMergeToUpsert } from '../../edit/edits/convertMergeToUpsert';
import type { MergeBranchSelector } from '../../edit/edits/modifyMergeBranch';
import { mergeMinVersionHint, supportsMerge } from '../../dialect/statementCapabilities';
import { AddButton } from '../common/AddButton';
import { DmlChangeTablePicker } from '../common/DmlChangeTablePicker';
import { RemoveButton } from '../common/RemoveButton';
import { VqeTooltip } from '../common/VqeTooltip';
import { DmlParseErrorPanel } from '../fallback/DmlParseErrorPanel';
import { ClauseCard } from '../select/ClauseCard';
import { AddWhenBranchMenu } from './AddWhenBranchMenu';
import { extractMergeOutline } from './extractMergeOutline';
import { MergeCapabilityNotice } from './MergeCapabilityNotice';
import { countMatchedNotMatchedBranches } from './mergeBranchTemplates';
import { normalizeOnPredicate } from './normalizeOnPredicate';
import { WhenBranchCard } from './WhenBranchCard';
import type { WhenBranch, WhenMatchKind } from './parseMergeBranch';
import { vqeCopy } from '../../ux/vqeCopy';
import { FULL_CANVAS_ROOT_CLASS } from '../common/canvasLayout';

export interface MergeRendererProps {
  readonly statement: StatementInfo;
  readonly bufferText: string;
  readonly options: ParseOptions;
  /** Escape to raw SQL. Surfaced on the parse-error panel; the workspace always threads it. */
  readonly onEditAsSql?: () => void;
  readonly drillTabId?: string;
  /**
   * Optional VQE-Edit context. When supplied, branch cards become editable.
   * The renderer maps each branch's flat source-order index to the (branchKind,
   * branchIndex) pair the SR-Edit MERGE planners expect.
   */
  readonly editContext?: VqeEditContext;
}

export function MergeRenderer({ statement, bufferText, options, onEditAsSql, editContext }: MergeRendererProps) {
  const outline = extractMergeOutline(bufferText, statement.range, options);

  if (
    !outline.hasMerge ||
    outline.targetText === null ||
    outline.sourceText === null ||
    outline.onStart === null ||
    outline.onEnd === null
  ) {
    return <DmlParseErrorPanel kind="merge" onEditAsSql={onEditAsSql ?? (() => undefined)} />;
  }

  // EDG-27: display-only normalization — strip a redundant outer paren pair
  // (Oracle's `MERGE … ON (…)`) so identical predicates render identically
  // across engines. The buffer slice / emitted SQL is untouched.
  const onText = normalizeOnPredicate(bufferText.slice(outline.onStart, outline.onEnd).trim(), options);
  const branchCount = outline.branches.length;
  const branchSelectors = computeBranchSelectors(outline.branches);
  const branchCounts = countMatchedNotMatchedBranches(outline.branches);

  // Convert-to-native-upsert (EDG-01/02/03/04) — only computed where the
  // capability notice actually shows: engines without MERGE (mysql/sqlite) and
  // the version-gated one (postgres, whose native upsert expresses the same
  // intent). `onConvert` is wired only when an edit context can write the
  // replacement back into the buffer.
  const engine = options.engine;
  const offersConvert = !supportsMerge(engine) || mergeMinVersionHint(engine) != null;
  const convert = offersConvert
    ? convertMergeToUpsert(outline, bufferText, engine, options)
    : null;
  let onConvert: (() => Promise<void>) | undefined;
  if (convert?.ok && editContext) {
    const generatedSql = convert.sql;
    onConvert = () =>
      applyMergeConvert({
        editContext,
        statementRange: statement.range,
        originalSource: bufferText,
        generatedSql,
      });
  }

  return (
    <div
      className={FULL_CANVAS_ROOT_CLASS}
      data-testid="vqe-merge-renderer"
      data-branch-count={branchCount}
    >
      <MergeCapabilityNotice
        engine={options.engine}
        onEditAsSql={onEditAsSql}
        convert={convert}
        onConvert={onConvert}
      />
      <MergeTargetCard target={outline.targetText} editContext={editContext} />
      <MergeSourceCard source={outline.sourceText} />
      <MergeOnCard onText={onText} />

      <ClauseCard
        title="When branches"
        testId="vqe-merge-when-branches"
        footer={
          <AddWhenBranchMenu
            editContext={editContext}
            options={options}
            matchedCount={branchCounts.matchedCount}
            notMatchedCount={branchCounts.notMatchedCount}
          />
        }
      >
        {branchCount === 0 ? (
          <p
            className="text-xs italic text-muted-foreground"
            data-testid="vqe-merge-when-branches-empty"
          >
            No WHEN branches parsed.
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {outline.branches.map((branch, idx) => (
              <li
                key={`${branch.start}-${idx}`}
                data-testid={`vqe-merge-when-branch-${idx}`}
                data-match-kind={branch.matchKind}
                data-action-kind={branch.action.kind}
              >
                <WhenBranchCard
                  branch={branch}
                  index={idx}
                  bufferText={bufferText}
                  options={options}
                  mergeTargetText={outline.targetText as string}
                  editContext={editContext}
                  mergeBranchSelector={branchSelectors[idx]}
                  totalBranchCount={branchCount}
                />
              </li>
            ))}
          </ul>
        )}
      </ClauseCard>
    </div>
  );
}

// Target, source and ON are one line each (`density="compact"`): each holds a
// single name or predicate, so a full padded card apiece left the MERGE view
// mostly empty space above the WHEN branches that carry the real content.
function MergeTargetCard({
  target,
  editContext,
}: {
  readonly target: string;
  readonly editContext?: VqeEditContext;
}) {
  return (
    <ClauseCard
      density="compact"
      title="Merge into"
      testId="vqe-merge-target"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs"
          data-testid="vqe-merge-target-text"
        >
          {target}
        </span>
        <DmlChangeTablePicker target="merge" editContext={editContext} />
      </div>
    </ClauseCard>
  );
}

function MergeSourceCard({ source }: { readonly source: string }) {
  return (
    <ClauseCard
      density="compact"
      title="Using"
      testId="vqe-merge-source"
      footer={<AddButton label={vqeCopy.actions.addSource} testId="vqe-merge-source-add" />}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs"
          data-testid="vqe-merge-source-text"
        >
          {source}
        </span>
        <RemoveButton testId="vqe-merge-source-remove" ariaLabel="Remove source" />
      </div>
    </ClauseCard>
  );
}

// Maps the renderer's flat source-order branch list to one
// MergeBranchSelector per branch, where matched / not-matched indices count
// up independently — mirroring the SR-Edit structure's split into
// `mergeWhenMatched[]` and `mergeWhenNotMatched[]`. `not-matched-by-source`
// rolls into the not-matched bucket since the SR-Edit extractor does not
// distinguish them in v1.
function computeBranchSelectors(
  branches: readonly WhenBranch[],
): readonly MergeBranchSelector[] {
  const out: MergeBranchSelector[] = [];
  let matchedCount = 0;
  let notMatchedCount = 0;
  for (const branch of branches) {
    const kind = bucketFor(branch.matchKind);
    if (kind === 'matched') {
      out.push({ branchKind: 'matched', branchIndex: matchedCount });
      matchedCount++;
    } else {
      out.push({ branchKind: 'not-matched', branchIndex: notMatchedCount });
      notMatchedCount++;
    }
  }
  return out;
}

function bucketFor(kind: WhenMatchKind): 'matched' | 'not-matched' {
  return kind === 'matched' ? 'matched' : 'not-matched';
}

function MergeOnCard({ onText }: { readonly onText: string }) {
  return (
    <ClauseCard
      density="compact"
      title="On"
      testId="vqe-merge-on"
      accessory={<RemoveButton testId="vqe-merge-on-remove" ariaLabel="Remove ON predicate" />}
    >
      <div className="flex flex-wrap items-center gap-2">
        <VqeTooltip content={vqeCopy.disabled.editingUnavailable}>
          <span
            className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs"
            data-testid="vqe-merge-on-text"
          >
            {onText}
          </span>
        </VqeTooltip>
      </div>
    </ClauseCard>
  );
}
